import { useEffect, useState } from "react";
import { cn } from "../lib/cn";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

/**
 * A line or two of text that says the whole of itself when it does not fit.
 *
 * Names in Pax are written by scenario authors rather than by us, and
 * "Mordovian Autonomous Soviet Socialist Republic" is one of the real ones.
 * Anywhere a name shares a line with anything else it will sometimes be cut,
 * and an ellipsis says only that something is missing. Pointing at it is how
 * the rest gets read.
 *
 * The hint is offered only on a line that is actually cut, because a hint
 * that repeats what is already on the screen teaches readers to ignore
 * hints. It is for the eye alone: the whole string is in the document either
 * way, so a screen reader has always had all of it.
 *
 * Two lines suit a title on a card, where one line cuts most real names and
 * an unbounded block of text breaks the grid. A word too long for the line,
 * such as a name written without spaces, breaks across it rather than being
 * cut at its edge.
 */
export function TruncatedText({
  text,
  className,
  lines = 1,
}: {
  text: string;
  className?: string;
  lines?: 1 | 2;
}) {
  const [line, setLine] = useState<HTMLElement | null>(null);
  const [cut, setCut] = useState(false);

  useEffect(() => {
    if (!line) return;
    // A line that fits can still measure a fraction wider than its box, so
    // the last pixel is not evidence of anything.
    const measure = () =>
      setCut(
        lines === 1
          ? line.scrollWidth > line.clientWidth + 1
          : line.scrollHeight > line.clientHeight + 1,
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(line);
    return () => observer.disconnect();
  }, [line, text, lines]);

  return (
    <Tooltip disabled={!cut}>
      <TooltipTrigger
        ref={setLine}
        render={<span />}
        className={cn(
          lines === 1 ? "block truncate" : "line-clamp-2 [overflow-wrap:anywhere]",
          className,
        )}
      >
        {text}
      </TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  );
}
